import { z } from "zod";

import { type Prisma } from "@/.zenstack/logical-prisma-client";

/**
 * Error-only schema for Description form (blocking validation)
 *
 * Contains only required field validation from .zmodel.
 * This validation blocks form submission.
 */
export const DescriptionErrorSchema = z.object({
  // Base validation from listing.zmodel: title @trim @length(5, 100)
  title: z
    .string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(100, "Title must be less than 100 characters"),

  // Description is Prisma JsonValue (Plate.js editor format)
  // Base: listing.zmodel defines as Json type
  description: z.unknown() as z.ZodType<Prisma.JsonValue>,
});

/**
 * Warning-only schema for Description form (non-blocking recommendations)
 *
 * Contains UX recommendations that don't block submission.
 * These are shown immediately to guide users toward best practices.
 */
export const DescriptionWarningSchema = z
  .object({
    title: z.string(),
    description: z.unknown() as z.ZodType<Prisma.JsonValue>,
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend longer titles for better SEO
    if (data.title.length < 20) {
      ctx.addIssue({
        code: "custom",
        message:
          "Consider adding more descriptive details to your title for better visibility",
        severity: "warning",
        path: ["title"],
      });
    }
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Check for meaningful description content
    if (data.description && typeof data.description === "object") {
      // Extract actual text content from Plate editor JSON structure
      const descriptionJson = JSON.stringify(data.description);
      const textContentRegex = /"text":\s*"([^"]*)"/g;
      let totalTextLength = 0;
      let match;

      while ((match = textContentRegex.exec(descriptionJson)) !== null) {
        totalTextLength += match[1]?.length ?? 0;
      }

      if (totalTextLength < 200) {
        ctx.addIssue({
          code: "custom",
          message:
            "Consider adding more details about the neighborhood, nearby amenities, and unique property features to attract more qualified tenants",
          severity: "warning",
          path: ["description"],
        });
      }
    }
  });

export type DescriptionFormData = z.infer<typeof DescriptionErrorSchema>;
